import { useQuery } from '@tanstack/react-query';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { useAuthStore } from '../../../stores/auth.store';
import { useModalStore } from '../../../stores/modal.store';
import { DesktopOnly } from '../../../components/ui/DesktopOnly';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { FATTURE_VISTE, VISTA_LABEL, type FattureVista } from '../../../lib/adminRoutes';
import { adminOverviewQuery, canReadAdmin, canWriteAdmin } from '../adminApi';
import { DocumentsSection, NewDocumentButton } from '../../invoices/DocumentsSection';
import { AdminHeader, StoricoNote, ViewTabs, useStorico, useVista, type ViewTab } from '../shared/AdminShell';
import { FattureDaIncassare } from './FattureDaIncassare';
import { FattureMobile } from './FattureMobile';
import { useIsMobile } from '../../../hooks/useIsMobile';
import '../admin.css';
import './fatture.css';

/**
 * Fatture (`/admin/fatture`): chi ci deve pagare, da quanto tempo, e i
 * documenti che emettiamo (proposta § 2.2, lotto 5).
 *
 * Viste (`?vista=`, etichette in lib/adminRoutes.ts, VISTA_LABEL):
 *   Fatture da incassare     (AMMINISTRAZIONE) le rate aperte dei clienti
 *   Fatture emesse da noi    (FATTURAZIONE)    fatture, note di credito,
 *                                              pre-fatture, avvisi
 *   Preventivi               (FATTURAZIONE)
 *
 * I permessi sono due moduli diversi: una vista che l'utente non può leggere
 * non si mostra (prima dava errori 403, mappa § 6). La prima vista che resta è
 * la predefinita.
 *
 * Azioni: "Nuovo documento" (Fatturazione) in intestazione; "Incasso in
 * contanti" nel menu ⋯ dell'intestazione (era un bottone doppio dentro la
 * lista, e non è il lavoro di tutti i giorni).
 */
const TITOLI: Record<FattureVista, string> = {
  incassare: 'Le fatture che i clienti non ci hanno ancora pagato',
  documenti: 'Le fatture che abbiamo emesso, con note di credito, pre-fatture e avvisi di pagamento',
  preventivi: 'Le proposte ai clienti, con lo stato della firma',
};

function livello(user: { permissions: Array<{ module: string; level: string }> } | null | undefined, mod: string): PermissionLevel {
  return (user?.permissions.find((p) => p.module === mod)?.level ?? PermissionLevel.NONE) as PermissionLevel;
}

/**
 * Sul telefono la pagina è FattureMobile (la risposta in cima, righe al posto
 * di schede, i gesti nel pannello della riga). Sul computer resta questa.
 */
export function FatturePage() {
  const isMobile = useIsMobile();
  return isMobile ? <FattureMobile /> : <FattureDesktop />;
}

function FattureDesktop() {
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const canAdmin = canReadAdmin(user);
  const canWrite = canWriteAdmin(user);
  const lvDocs = livello(user, ModuleKey.FATTURAZIONE);
  const canReadDocs = hasAtLeast(lvDocs, PermissionLevel.READ);
  // Emettere documenti è della Fatturazione, non dell'Amministrazione
  const canWriteDocs = hasAtLeast(lvDocs, PermissionLevel.WRITE);

  const permesse = FATTURE_VISTE.filter((k) => (k === 'incassare' ? canAdmin : canReadDocs));
  const [vista, setVista] = useVista<FattureVista>(permesse.length ? permesse : FATTURE_VISTE);
  const storico = useStorico();
  const { data: ov } = useQuery({ ...adminOverviewQuery, enabled: canAdmin });

  const inRitardo = ov?.fatture.overdueCount ?? 0;
  const tabs: ViewTab<FattureVista>[] = permesse.map((k) => ({
    key: k,
    label: VISTA_LABEL.fatture[k],
    title: k === 'incassare' && inRitardo > 0 ? `${inRitardo === 1 ? '1 fattura' : `${inRitardo} fatture`} in ritardo col pagamento` : TITOLI[k],
    count: k === 'incassare' ? inRitardo : undefined,
  }));

  const viewTabs = <ViewTabs tabs={tabs} active={vista} onChange={setVista} />;

  const contanti: ActionItem = {
    key: 'contanti', label: 'Registra un incasso in contanti', icon: 'banknote',
    onClick: () => openModal('newCashEntry', { direction: 'IN' }),
    hidden: !canWrite,
  };

  if (!permesse.length) {
    return (
      <div className="page adm-page">
        <AdminHeader title="Fatture" />
        <EmptyState brand title="Le fatture non sono per il tuo profilo" description="Per vederle serve l'accesso all'Amministrazione o alla Fatturazione: chiedilo a chi gestisce gli utenti." />
      </div>
    );
  }

  return (
    <div className="page adm-page">
      <AdminHeader
        title="Fatture"
        sub="Le fatture che i clienti ci devono ancora pagare, quelle emesse da noi e i preventivi."
        actions={<>{storico.button}{canWrite && <ActionsMenu items={[contanti]} />}</>}
        menu={[storico.menuItem, contanti]}
        primary={canWriteDocs ? <NewDocumentButton /> : undefined}
      />

      {storico.on && <StoricoNote year={storico.year} onOff={() => storico.set(false)} />}

      {/* Ritmo della pagina (rifinitura.md §1.5): frase e fascia della vista,
          poi le linguette, poi i filtri. Le linguette le mette la vista, perché
          frase e fascia cambiano con lei; sul telefono così linguette e tasto
          Filtri restano sulla stessa riga (mobile.md 4bis.4). */}
      {vista === 'incassare' && canAdmin && (
        <FattureDaIncassare canWrite={canWrite} canReadDocs={canReadDocs} showHistory={storico.on} tabs={viewTabs} />
      )}
      {vista !== 'incassare' && viewTabs}
      {vista === 'documenti' && canReadDocs && <DocumentsSection scope="fiscali" embedded showHistory={storico.on} />}
      {vista === 'preventivi' && canReadDocs && <DocumentsSection scope="preventivi" embedded showHistory={storico.on} />}

      {/* Sul telefono l'azione principale sta in fondo (mobile.md §4). Creare
          un documento però è una procedura lunga: dal telefono lo si dice, e
          solo nelle viste dei documenti. In Fatture da incassare il gesto del
          telefono è "Segna incassata", dentro la scheda. */}
      {canWriteDocs && vista !== 'incassare' && (
        <DesktopOnly
          cosa="La creazione di fatture e preventivi"
          perche="è una procedura a più passaggi con righe, imposte e scadenze; qui puoi consultare le fatture che ci sono e segnarle incassate."
        >
          <div className="m-actionbar m-only">
            <NewDocumentButton />
          </div>
        </DesktopOnly>
      )}
    </div>
  );
}
